import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { generateClassName } from '@klv/shared/utils/functions';
import { useTranslation } from '@klv/hooks/useTranslation';
import { InfoToolTip } from './info-tooltip';
import * as styles from './styles/slider.module.css';
import type { ISlider } from './types';

const Slider = ({
  className = '',
  range,
  headline,
  subText,
  subTextInfo,
  subTextInfoHeader,
  defaultValue,
  context,
  onChange,
  variant,
  ...props
}: ISlider) => {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  
  const platformCard = variant === 'platform';
  
  const getInitialValue = () => {
    if (typeof window !== 'undefined' && context) {
      const params = new URLSearchParams(window.location.search);
      const urlValue = params.get(context);
      if (urlValue) {
        const numericValue = Number(urlValue);
        const isValid = range.some((r) => r.value === numericValue);
        if (isValid) {
          return numericValue;
        }
      }
    }
    return defaultValue ?? range[0].value;
  };

  const [rangeValue, setRangeValue] = useState(getInitialValue);
  const [sliderValue, setSliderValue] = useState(() => range.findIndex(item => item.value === getInitialValue()));

  const updateValues = useCallback((newRangeValue: number) => {
    const newSliderValue = range.findIndex(item => item.value === newRangeValue);
    setRangeValue(newRangeValue);
    setSliderValue(newSliderValue);
    if (onChange) onChange(newRangeValue);
  }, [range, onChange]);

  const maxSteps = range.length - 1;

  const labelRef = useRef<HTMLDivElement>(null);
  const [labelHeight, setLabelHeight] = useState(0);
  const updateLabelHeight = useCallback(() => {
    if (labelRef.current) setLabelHeight(labelRef.current.offsetHeight);
  }, []);
  useEffect(() => {
    updateLabelHeight();
    window.addEventListener('resize', updateLabelHeight);
    return () => window.removeEventListener('resize', updateLabelHeight);
  }, [updateLabelHeight]);

  useEffect(() => {
    if (context && rangeValue !== null) {
      const url = new URL(window.location.href);
      url.searchParams.set(context, rangeValue.toString());
      window.history.replaceState({}, '', url);
    }
  }, [context, rangeValue]);

  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = Number(e.target.value);
      const closestRangeValue = range[Math.round(rawValue)].value;
      updateValues(closestRangeValue);
    },
    [range, updateValues],
  );

  const getTrackBackground = (value: number) => {
    const percentage = (value / maxSteps) * 100;
    return `linear-gradient(to right, #744A6E ${percentage}%, var(--color-charcoal) ${percentage}%)`;
  };

  const scrollPosition = sliderValue !== -1 ? sliderValue * labelHeight * -1 : 0;

  return (
    <div className={generateClassName([className, styles.slider])} {...props}>
      <h6 className={`h6 ${styles.headline}`}>{headline}</h6>
      <div className={styles.subTextContainer}>
        <InfoToolTip
          subText={subText}
          subTextInfoHeader={subTextInfoHeader}
          subTextInfo={subTextInfo}
          variant={platformCard ? 'platform' : 'default'}
        />
        <div className={platformCard ? styles.platformRangeLabelContainer : styles.rangeLabelContainer}>
          <div
            className={styles.rangeLabelList}
            style={{ transform: `translateY(${scrollPosition}px)` }}
          >
            {range.map((item, index) => (
              <div
                key={index}
                ref={index === 0 ? labelRef : null}
                className={platformCard ? styles.platformRange : styles.rangeLabel}
              >
                {item.label.split(' – ').map((num, i) => {
                  const cleanedNum = num.replace(/[^\d-]/g, '');
                  const formattedNum = new Intl.NumberFormat(locale).format(Number(cleanedNum));
                  return i === 0 ? formattedNum : ` – ${formattedNum}`;
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      <input
        type="range"
        min={0}
        max={maxSteps}
        step={0.01}
        value={sliderValue}
        onChange={handleSliderChange}
        className={styles.customSlider}
        style={{ background: getTrackBackground(sliderValue) }}
        title="Slider"
      />
    </div>
  );
};

export default Slider;
